import { AnalyticsEvent, ATTENDANCE_SAVED } from '../events'
import { AttendanceReport, summarizeReport } from './utils/summarize-report'

export interface AttendanceSavedProperties {
  student_count: number
  // non-presence marks saved. The screen lets a user save a sheet without
  // touching anything, so 0 is a real possibility and `has_changes` exists to
  // keep those saves out of the way
  mark_count: number
  absence_count: number
  late_count: number
  justified_count: number
  early_pick_up_count: number
  has_changes: boolean
  is_double_shift: boolean
  // the saved day was before today: a backlog entry rather than a live check
  is_backdated: boolean
  term_id: string
  stage_id: string
  division_id: string
}

// what the event cannot infer from the report itself, all of it already
// selected by the time the attendance screen allows a save
export interface AttendanceSavedContext {
  termId: string
  stageId: string
  divisionId: string
  studentCount: number
  isDoubleShift: boolean
  isBackdated: boolean
}

/**
 * Builds the `attendance_saved` event from the report the API just accepted, so
 * the payload describes what was really stored instead of the form state.
 * Tenant, role and user id are not repeated here: they are user properties set
 * by `identifyUser`.
 *
 * One event per save, not per student mark: marks are only real once saved, and
 * the per-student detail is carried by the counts above.
 */
export function buildAttendanceSavedEvent (report: AttendanceReport, context: AttendanceSavedContext): AnalyticsEvent {
  const summary = summarizeReport(report)

  const properties: AttendanceSavedProperties = {
    student_count: context.studentCount,
    ...summary,
    has_changes: summary.mark_count > 0,
    is_double_shift: context.isDoubleShift,
    is_backdated: context.isBackdated,
    term_id: context.termId,
    stage_id: context.stageId,
    division_id: context.divisionId
  }

  return {
    name: ATTENDANCE_SAVED,
    properties
  }
}
